/** The quick actions every value on an order surface offers — the order's
 *  own references (ref, custom id, state, active, contact, account) plus the
 *  booking-level builders it shares with the booking surfaces (supplier /
 *  reseller refs, integration, restech account). `order=` on /bookings lists
 *  the order's bookings; `custom=` and `contact_email=` are order-level
 *  namespaces on /orders. */
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  bookingsHref,
  cond,
  deliveryLogsHref,
  idCond,
  importRunsHref,
  ordersHref,
  sessionsHref,
} from '@/utils/gridLinks';

import {
  emailActions,
  integrationActions,
  type BookingCtx,
} from '@/features/bookings/overview/bookingLinks';

export type OrderCtx = BookingCtx;

const tenantOf = (ctx: OrderCtx) => ctx.tenantId ?? null;

export function orderRefActions(ref: string, ctx: OrderCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(ref, 'Copy order ref'),
    act.filter(bookingsHref([cond('order', ref)], t), 'Bookings in this order'),
    act.filter(ordersHref([cond('order', ref)], t), 'This order in the grid'),
    act.filter(deliveryLogsHref([cond('order', ref)], { tenant: t }), 'Delivery logs for this order'),
    act.filter(importRunsHref([cond('order', ref, 'exact')], t), 'Import runs for this order'),
  ];
}

export function orderIdActions(id: number): ValueAction[] {
  return [act.copy(String(id), 'Copy order id')];
}

export function orderCustomIdActions(customId: string, ctx: OrderCtx): ValueAction[] {
  return [
    act.copy(customId, 'Copy custom id'),
    act.filter(ordersHref([cond('custom', customId)], tenantOf(ctx)), 'Orders with this custom id'),
  ];
}

export function orderStateActions(label: string, ctx: OrderCtx): ValueAction[] {
  return [act.filter(ordersHref([cond('order_state', label)], tenantOf(ctx)), `Orders in state "${label}"`)];
}

export function orderActiveActions(active: boolean, ctx: OrderCtx): ValueAction[] {
  const v = active ? 'yes' : 'no';
  return [act.filter(ordersHref([cond('active', v, 'exact')], tenantOf(ctx)), active ? 'Active orders' : 'Inactive orders')];
}

/** The order's contact email: orders by contact email, plus the booking-side
 *  email searches (bookings / delivery logs / guest accounts). */
export function contactEmailActions(email: string, ctx: OrderCtx): ValueAction[] {
  return [
    act.filter(ordersHref([cond('contact_email', email)], tenantOf(ctx)), 'Orders with this contact email'),
    ...emailActions(email, ctx),
  ];
}

/** The account the order belongs to (OrderDetail.user). */
export function orderUserActions(user: { id: number; email: string | null }, ctx: OrderCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.open(`/users/${user.id}`, 'Open user'),
    ...(user.email ? [act.mailto(user.email), act.copy(user.email, 'Copy email')] : []),
    act.filter(ordersHref([idCond('user', user.id)], t), 'Orders of this user'),
    act.filter(bookingsHref([idCond('user', user.id)], t), 'Bookings of this user'),
    act.filter(sessionsHref({ user: user.id }), 'Sessions of this user'),
  ];
}

export function orderIntegrationActions(codename: string, ctx: OrderCtx): ValueAction[] {
  return [
    act.filter(ordersHref([cond('integration', codename)], tenantOf(ctx)), 'Orders from this integration'),
    ...integrationActions(codename, ctx),
  ];
}

/** Created/updated ±presets on the ORDERS grid. */
export function orderTimestampActions(ns: 'created' | 'updated', iso: string, ctx: OrderCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return act.timeRanges(iso, (range) => ordersHref([range], t), ns);
}
